import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Heart, MoreHorizontal, Send } from 'lucide-react';
import { Story, StoryGroup } from '../../types/story';
import { storyService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface StoryViewerProps {
  storyGroups: StoryGroup[];
  initialGroupIndex: number;
  initialStoryIndex?: number;
  onClose: () => void;
}

const StoryViewer: React.FC<StoryViewerProps> = ({
  storyGroups,
  initialGroupIndex,
  initialStoryIndex = 0,
  onClose
}) => {
  const { user } = useAuth();
  const [currentGroupIndex, setCurrentGroupIndex] = useState(initialGroupIndex);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(initialStoryIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [replyText, setReplyText] = useState('');
  const progressRef = useRef<NodeJS.Timeout>();

  const currentGroup = storyGroups[currentGroupIndex];
  const currentStory = currentGroup?.stories[currentStoryIndex];

  const reactions = ['❤️', '😂', '😮', '😢', '😡', '👏', '🔥', '💯'];

  useEffect(() => {
    if (currentStory && !isPaused) {
      // Mark story as viewed
      handleViewStory(currentStory.id);
      
      // Start progress timer
      startProgress();
    }

    return () => {
      if (progressRef.current) {
        clearInterval(progressRef.current);
      }
    };
  }, [currentStory, isPaused]);

  const startProgress = () => {
    if (progressRef.current) {
      clearInterval(progressRef.current);
    }

    const duration = currentStory?.duration || 15;
    const interval = 50; // Update every 50ms
    const increment = (interval / 1000) / duration * 100;

    setProgress(0);
    
    progressRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;
        if (newProgress >= 100) {
          nextStory();
          return 0;
        }
        return newProgress;
      });
    }, interval);
  };

  const handleViewStory = async (storyId: string) => {
    try {
      await storyService.viewStory(storyId);
    } catch (error) {
      console.error('Failed to mark story as viewed:', error);
    }
  };

  const nextStory = () => {
    if (currentStoryIndex < currentGroup.stories.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
    } else if (currentGroupIndex < storyGroups.length - 1) {
      setCurrentGroupIndex(prev => prev + 1);
      setCurrentStoryIndex(0);
    } else {
      onClose();
    }
  };

  const previousStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
    } else if (currentGroupIndex > 0) {
      setCurrentGroupIndex(prev => prev - 1);
      const prevGroup = storyGroups[currentGroupIndex - 1];
      setCurrentStoryIndex(prevGroup.stories.length - 1);
    }
  };

  const handleReaction = async (emoji: string) => {
    if (!currentStory) return;
    
    try {
      await storyService.reactToStory(currentStory.id, emoji);
      setShowReactions(false);
    } catch (error) {
      console.error('Failed to react to story:', error);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !currentStory) return;
    
    try {
      await storyService.replyToStory(currentStory.id, replyText);
      toast.success('Reply sent!');
      setReplyText('');
    } catch (error) {
      console.error('Failed to reply to story:', error);
      toast.error('Failed to send reply');
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (!currentStory || !currentGroup) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
      {/* Progress Bars */}
      <div className="absolute top-4 left-4 right-4 z-10">
        <div className="flex space-x-1">
          {currentGroup.stories.map((_, index) => (
            <div key={index} className="flex-1 h-1 bg-gray-600 rounded-full overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-100 ease-linear"
                style={{
                  width: index < currentStoryIndex 
                    ? '100%' 
                    : index === currentStoryIndex 
                      ? `${progress}%` 
                      : '0%'
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="absolute top-8 left-4 right-4 z-10 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-600 flex items-center justify-center">
            {currentGroup.user.avatar ? (
              <img
                src={currentGroup.user.avatar}
                alt={currentGroup.user.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white text-xs font-medium">
                {getInitials(currentGroup.user.name)}
              </span>
            )}
          </div>
          <div>
            <p className="text-white font-medium text-sm">{currentGroup.user.name}</p>
            <p className="text-gray-300 text-xs">
              {Math.floor((Date.now() - new Date(currentStory.createdAt).getTime()) / (1000 * 60 * 60))}h ago
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full"
          >
            {isPaused ? '▶️' : '⏸️'}
          </button>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Story Content */}
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Navigation Areas */}
        <button
          onClick={previousStory}
          className="absolute left-0 top-0 w-1/3 h-full z-10 flex items-center justify-start pl-4"
        >
          <ChevronLeft className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
        </button>
        
        <button
          onClick={nextStory}
          className="absolute right-0 top-0 w-1/3 h-full z-10 flex items-center justify-end pr-4"
        >
          <ChevronRight className="w-8 h-8 text-white opacity-0 hover:opacity-100 transition-opacity" />
        </button>

        {/* Story Display */}
        <div className="w-full h-full flex items-center justify-center">
          {currentStory.type === 'image' && currentStory.url && (
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={currentStory.url}
                alt="Story"
                className="max-w-full max-h-full object-contain"
              />
              {/* Text overlay */}
              {currentStory.text && (
                <div className={`absolute left-0 right-0 flex justify-center px-4 ${
                  currentStory.textPosition === 'top' ? 'top-16' :
                  currentStory.textPosition === 'center' ? 'top-1/2 -translate-y-1/2' :
                  'bottom-20'
                }`}>
                  <div className="bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2.5 w-full max-w-full">
                    <p
                      className={`text-sm sm:text-base leading-relaxed text-center whitespace-pre-wrap break-words ${
                        currentStory.font === 'bold' ? 'font-bold' :
                        currentStory.font === 'italic' ? 'italic' :
                        currentStory.font === 'handwriting' ? 'font-serif' : ''
                      }`}
                      style={{ color: currentStory.textColor || '#FFFFFF' }}
                    >
                      {currentStory.text}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {currentStory.type === 'video' && currentStory.url && (
            <div className="relative w-full h-full flex items-center justify-center">
              <video
                src={currentStory.url}
                autoPlay
                muted
                className="max-w-full max-h-full object-contain"
              />
              {/* Text overlay */}
              {currentStory.text && (
                <div className={`absolute left-0 right-0 flex justify-center px-4 ${
                  currentStory.textPosition === 'top' ? 'top-16' :
                  currentStory.textPosition === 'center' ? 'top-1/2 -translate-y-1/2' :
                  'bottom-20'
                }`}>
                  <div className="bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2.5 w-full max-w-full">
                    <p
                      className={`text-sm sm:text-base leading-relaxed text-center whitespace-pre-wrap break-words ${
                        currentStory.font === 'bold' ? 'font-bold' :
                        currentStory.font === 'italic' ? 'italic' :
                        currentStory.font === 'handwriting' ? 'font-serif' : ''
                      }`}
                      style={{ color: currentStory.textColor || '#FFFFFF' }}
                    >
                      {currentStory.text}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {currentStory.type === 'text' && (
            <div
              className="w-full h-full flex items-center justify-center p-8"
              style={{
                backgroundColor: currentStory.backgroundColor || '#000000',
                color: currentStory.textColor || '#FFFFFF'
              }}
            >
              <p className={`text-center text-2xl sm:text-3xl leading-relaxed max-w-lg ${
                currentStory.font === 'bold' ? 'font-bold' :
                currentStory.font === 'italic' ? 'italic' :
                currentStory.font === 'handwriting' ? 'font-serif' : ''
              }`}>
                {currentStory.text}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="absolute bottom-4 left-4 right-4 z-10">
        {/* Reactions */}
        {showReactions && (
          <div className="mb-4 flex justify-center">
            <div className="bg-black bg-opacity-50 rounded-full px-4 py-2 flex space-x-3">
              {reactions.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(emoji)}
                  className="text-2xl hover:scale-110 transition-transform"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Reply Input */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowReactions(!showReactions)}
            className="p-3 bg-white bg-opacity-20 text-white rounded-full hover:bg-opacity-30"
          >
            <Heart className="w-5 h-5" />
          </button>
          
          <div className="flex-1 flex items-center space-x-2 bg-white bg-opacity-20 rounded-full px-4 py-2">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`Reply to ${currentGroup.user.name.split(' ')[0]}...`}
              className="flex-1 bg-transparent text-white placeholder-gray-300 focus:outline-none"
              onKeyPress={(e) => e.key === 'Enter' && handleReply()}
            />
            <button
              onClick={handleReply}
              disabled={!replyText.trim()}
              className="p-1 text-white disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryViewer;